import { Target, Eye, Award, ShieldCheck, Lightbulb, Rocket } from "lucide-react";
import PageHead from "../components/PageHead";
import ParticleField from "../components/ParticleField";
import Reveal from "../components/Reveal";
import { PrimaryButton, GhostButton } from "../components/Buttons";
import { heroBgStyle } from "../lib/heroBg";
import { team } from "../data/site";

const values = [
  { icon: Award, title: "Excellence", body: "We hold every deliverable to a standard that earns trust." },
  { icon: ShieldCheck, title: "Integrity", body: "Honest counsel and transparent partnerships, always." },
  { icon: Lightbulb, title: "Innovation", body: "We blend craft, technology and storytelling to find new angles." },
  { icon: Rocket, title: "Impact", body: "Work that moves business outcomes — not just vanity metrics." },
];

const stats = [
  { num: "10+", suffix: "Years", label: "Experience" },
  { num: "50+", label: "Projects Delivered" },
  { num: "100%", label: "Client Satisfaction" },
  { num: "Pan-African", label: "Reach", small: true },
];

const principles = [
  { kicker: "01", title: "We start with the problem", body: "Every brief begins with a real conversation. We listen, ask uncomfortable questions, and only then propose a path. No solutions in search of a problem." },
  { kicker: "02", title: "We treat your brand like our own", body: "Your customers don't care that we're a vendor. Neither do we. We sit on your side of the table from day one." },
  { kicker: "03", title: "Craft is non-negotiable", body: "Pixel alignment, code quality, sound mix, paper stock — the small things compound. We sweat them so your audience never has to." },
  { kicker: "04", title: "Shipping is the strategy", body: "Plans are nothing. Action is everything. We move in two-week beats and put real work in front of real users — fast." },
];

export default function About() {
  return (
    <main data-testid="about-page">
      <PageHead
        title="About | Digital Archdeacon Media Ltd"
        description="A team of ICT professionals and media creatives bridging technology and storytelling — across Africa and beyond."
      />

      {/* Hero — same bg image as home, smaller */}
      <section className="relative pt-32 pb-12 overflow-hidden" style={heroBgStyle} data-testid="about-hero">
        <ParticleField density={45} />
        <div className="absolute inset-0 bg-grid opacity-20 pointer-events-none" />
        <div className="absolute -top-24 -left-20 h-[420px] w-[420px] rounded-full bg-electric/25 blur-3xl pointer-events-none" />
        <div className="absolute top-1/2 -right-32 h-[420px] w-[420px] rounded-full bg-gold/15 blur-3xl pointer-events-none" />
        <div className="container-x relative">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">About us</span>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="h-display text-[clamp(2.5rem,6.5vw,5.25rem)] mt-4 max-w-5xl">
              People-first.<br/>
              <span className="text-gradient-blue">Tech-fluent.</span><br/>
              Story-driven.
            </h1>
          </Reveal>
          <Reveal delay={220}>
            <p className="mt-8 max-w-2xl text-lg sm:text-xl text-[#CBD5E1] leading-relaxed">
              We're a studio of ICT engineers and media creatives building digital products, films and brand systems for organisations that take craft seriously.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Story split — light blue */}
      <section className="section-pad section-light-blue" data-testid="about-story">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-5 lg:sticky lg:top-28">
            <span className="text-xs uppercase tracking-[0.3em] text-electric font-semibold">Our story</span>
            <h2 className="h-display text-4xl sm:text-5xl md:text-6xl text-[#0A0F2C] mt-4">
              Bridging technology and storytelling.
            </h2>
            <p className="mt-6 text-[#475569] text-lg leading-relaxed">
              One mission. From the first business cards we ever printed to the multi-cam live streams we run today, the work has always been about one thing — making people understand what you actually do, and why it matters.
            </p>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={120}>
            <div className="space-y-6 text-lg text-[#1E293B] leading-relaxed">
              <p>
                Digital Archdeacon Media Ltd was founded with a simple conviction: that technology and media, when practised with discipline, can lift any organisation that uses them well. Churches that need to reach the next generation. NGOs that need to fundraise across borders. SMEs that need to look as serious online as they are offline. Companies, businesses, schools, and individuals — each of them deserves work that actually moves the needle.
              </p>
              <p>
                We started small — a press, a couple of cameras, and a hard-earned reputation for sweating the details. We've grown into a full-stack studio: software engineers shipping enterprise platforms, designers creating brand systems used daily by thousands of customers, a media team broadcasting events seen across continents.
              </p>
              <p>
                What hasn't changed is the belief that the smallest details — a paragraph rewritten, a frame recut, a button realigned — are what separate work that's forgotten from work that compounds. We exist to do that work, again and again, for organisations that care.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mission & Vision — bold cards */}
      <section className="section-pad" data-testid="about-mission-vision">
        <div className="container-x grid md:grid-cols-2 gap-7">
          <Reveal>
            <div className="glass p-10 h-full relative overflow-hidden">
              <div className="absolute -top-16 -right-10 h-44 w-44 rounded-full bg-electric/20 blur-3xl" />
              <Target className="h-9 w-9 text-electric relative" />
              <p className="text-xs uppercase tracking-[0.25em] text-electric mt-5">Mission</p>
              <h3 className="h-display text-3xl md:text-4xl mt-3">Empower organisations through innovative technology &amp; compelling media.</h3>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="glass p-10 h-full relative overflow-hidden">
              <div className="absolute -top-16 -right-10 h-44 w-44 rounded-full bg-gold/20 blur-3xl" />
              <Eye className="h-9 w-9 text-gold relative" />
              <p className="text-xs uppercase tracking-[0.25em] text-gold mt-5">Vision</p>
              <h3 className="h-display text-3xl md:text-4xl mt-3">To be Africa's most trusted ICT and media solutions partner.</h3>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Principles — vertical list, dark */}
      <section className="section-pad" data-testid="about-principles">
        <div className="container-x">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">How we work</span>
            <h2 className="h-display text-5xl md:text-6xl mt-3">Four principles. No exceptions.</h2>
          </Reveal>
          <div className="mt-14 grid gap-1">
            {principles.map((p, i) => (
              <Reveal key={p.title} delay={i * 80}>
                <div className="grid md:grid-cols-12 gap-6 items-start py-7 border-t border-white/10">
                  <p className="md:col-span-2 font-heading text-electric text-3xl font-bold">{p.kicker}</p>
                  <h3 className="md:col-span-4 h-display text-2xl md:text-3xl">{p.title}</h3>
                  <p className="md:col-span-6 text-[#CBD5E1] text-lg leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
            <div className="border-t border-white/10" />
          </div>
        </div>
      </section>

      {/* Core values */}
      <section className="section-pad" data-testid="about-values">
        <div className="container-x">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">Core values</span>
            <h2 className="h-display text-5xl md:text-6xl mt-3">What we won't compromise on.</h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v, idx) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} delay={idx * 90}>
                  <div className="glass p-7 h-full" data-testid={`value-${v.title.toLowerCase()}`}>
                    <div className="h-11 w-11 rounded-xl grid place-items-center bg-electric/10 border border-electric/20 text-electric">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold mt-5">{v.title}</h3>
                    <p className="text-[#CBD5E1] mt-3 text-sm leading-relaxed">{v.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team expanded — white section */}
      <section className="section-pad section-white" data-testid="about-team">
        <div className="container-x">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-electric font-semibold">Our team</span>
            <h2 className="h-display text-5xl md:text-6xl text-[#0A0F2C] mt-3">The people behind the work.</h2>
          </Reveal>
          <div className="mt-14 grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
            {team.map((m, idx) => (
              <Reveal key={m.name} delay={idx * 90} className="h-full">
                <article
                  className="group rounded-2xl bg-white border border-[#1E6FFF]/20 p-5 h-full flex flex-col hover:-translate-y-2 hover:border-electric hover:shadow-[0_18px_38px_rgba(30,111,255,0.18)] transition-all"
                  data-testid={`about-team-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <div className="rounded-2xl overflow-hidden aspect-square ring-1 ring-electric/30">
                    <img src={m.photo} alt={m.name} loading="lazy" className="h-full w-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold mt-5 text-[#0A0F2C]">{m.name}</h3>
                  <p className="text-electric text-sm font-semibold tracking-wide">{m.role}</p>
                  <p className="mt-3 text-[#475569] text-sm leading-relaxed">{m.bio}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-pad" data-testid="about-stats">
        <div className="container-x">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">Why choose us</span>
            <h2 className="h-display text-5xl md:text-6xl mt-3">Numbers that back the craft.</h2>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((s, idx) => (
              <Reveal key={s.label} delay={idx * 80}>
                <div className="glass p-7 text-center" data-testid={`stat-${idx}`}>
                  <p className={`font-heading font-bold text-gold tracking-tight whitespace-nowrap ${
                    s.small ? "text-3xl md:text-4xl" : "text-4xl md:text-5xl"
                  }`}>
                    {s.num}{s.suffix ? <span className="ml-1 text-2xl md:text-3xl text-white">{s.suffix}</span> : null}
                  </p>
                  <p className="mt-3 text-[#CBD5E1] uppercase tracking-[0.18em] text-xs">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Unique About-page CTA: "Coffee on us" */}
      <section className="section-pad" data-testid="about-cta">
        <div className="container-x">
          <Reveal>
            <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-cta-gradient p-12 md:p-16">
              <div className="absolute -top-32 -right-20 h-80 w-80 rounded-full bg-electric/40 blur-3xl" />
              <div className="absolute -bottom-32 -left-24 h-80 w-80 rounded-full bg-gold/20 blur-3xl" />
              <div className="relative max-w-3xl">
                <span className="text-xs uppercase tracking-[0.3em] text-gold font-semibold">Coffee on us</span>
                <h2 className="h-display text-5xl md:text-6xl mt-3">
                  Like the way we think? Let's talk.
                </h2>
                <p className="mt-5 text-lg text-[#CBD5E1] max-w-2xl">
                  No sales pitch — just 30 minutes about your business and what's standing in the way of it growing online. Bring your hardest question.
                </p>
                <div className="mt-9 flex flex-wrap gap-4">
                  <PrimaryButton to="/contact" testId="about-cta-contact">Book a Conversation</PrimaryButton>
                  <GhostButton to="/services" testId="about-cta-services">Browse Our Services</GhostButton>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
